import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const KINDS = [
  { key: "qc", flag: "qc_today", label: "QC today" },
  { key: "pt", flag: "pt_recent", label: "PT cycle (120 days)" },
  { key: "sop", flag: "sop_current", label: "Active SOP" },
];

export const EquipmentCoverage = ({ onCreated }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/equipment/coverage");
      setRows(data);
    } catch (e) { console.warn("equipment coverage unavailable:", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createDraft = async (analyzer, kind) => {
    setBusy(`${analyzer}-${kind}`);
    try {
      await api.post(`/equipment/coverage/${encodeURIComponent(analyzer)}/draft?kind=${kind}`);
      toast.success(`Draft created for ${analyzer}`);
      await load();
      onCreated?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (rows.length === 0) return null;

  return (
    <div className="mt-6 rounded-md border border-[#E5E7EB] bg-white" data-testid="equipment-coverage">
      <div className="px-4 py-3 border-b border-[#E5E7EB]">
        <h2 className="font-heading font-bold text-sm text-[#111827]">Equipment coverage</h2>
        <p className="text-xs text-[#6B7280] mt-0.5">
          Every machine logged here is registered across QC, Proficiency Testing and SOP records. Missing
          cover can be created as a pre-filled draft in one click.
        </p>
      </div>
      <div className="divide-y divide-[#E5E7EB]">
        {rows.map((m) => (
          <div key={m.analyzer} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
            data-testid={`coverage-${m.analyzer}`}>
            <div className="min-w-[200px]">
              <p className="text-sm font-medium text-[#111827]">{m.analyzer}</p>
              <p className="text-xs text-[#6B7280]">
                {[m.serial_number, m.department, m.equipment_status].filter(Boolean).join(" · ") || "No details yet"}
                {m.calibration_overdue && <span className="text-[#C92A2A]"> · calibration overdue</span>}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 ml-auto">
              {KINDS.map((k) => (
                m[k.flag] ? (
                  <span key={k.key} className="inline-flex items-center gap-1 text-xs text-[#2E7D32]"
                    data-testid={`coverage-ok-${m.analyzer}-${k.key}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> {k.label}
                  </span>
                ) : (
                  <Button key={k.key} variant="outline" size="sm" disabled={busy === `${m.analyzer}-${k.key}`}
                    onClick={() => createDraft(m.analyzer, k.key)}
                    data-testid={`coverage-create-${m.analyzer}-${k.key}`}>
                    <AlertTriangle className="h-3.5 w-3.5 mr-1.5 text-[#B45309]" />
                    {k.label} missing <Plus className="h-3 w-3 ml-1" />
                  </Button>
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
